import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, ArrowRight, ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { validateSignInForm } from '../utils/validation';
import '../styles/SignIn.css';

const SignIn = () => {
  const navigate = useNavigate();
  const { signin, isLoading, error: authError, clearError } = useAuth();
  const { error: showError } = useToast();

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [localError, setLocalError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (localError) {
      setLocalError('');
    }
  };

  const handleSubmit = async (e) => {
    console.log('>>> handleSubmit fired, preventDefault next');
    e.preventDefault();
    setLocalError('');
    clearError();

    const validation = validateSignInForm(formData.email, formData.password);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    try {
      // Step 1 of 2: password check only. Backend sends an OTP and returns a
      // challengeToken — no session is created yet. VerifyOtp.jsx handles step 2.
      const response = await signin(formData.email, formData.password);

      if (response && response.success) {
        navigate('/verify-otp', {
          state: {
            purpose: 'signin',
            email: response.data.email,
            challengeToken: response.data.challengeToken,
            devCode: response.data.devCode,
          },
        });
      }
    } catch (err) {
      const errorMsg = authError || err?.message || 'Sign in failed. Please try again.';
      setLocalError(errorMsg);
      showError(errorMsg);
      console.error('Sign in error:', err);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <motion.div
      className="signin-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="signin-container">
        {/* Left Side - Branding */}
        <motion.div
          className="signin-brand"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="brand-content">
            <Link to="/" className="brand-logo">
              <img
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt="Effective Global Staff"
                className="brand-logo-img"
              />
            </Link>
            <h1>Welcome Back</h1>
            <p>Sign in to your Effective Global Staff account to manage your payroll</p>

            <div className="brand-benefits">
              {['Secure & Compliant', 'Real-Time Updates', '24/7 Support'].map((benefit, idx) => (
                <div key={idx} className="benefit-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            <div className="brand-bg">
              <div className="blob blob-1"></div>
              <div className="blob blob-2"></div>
            </div>
          </div>
        </motion.div>

        {/* Right Side - Form */}
        <motion.div
          className="signin-form-section"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <motion.form
            className="signin-form"
            onSubmit={handleSubmit}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Back to Home */}
            <motion.div variants={itemVariants} style={{ marginBottom: '1rem' }}>
              <Link to="/" className="back-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', color: '#6b7280', textDecoration: 'none' }}>
                <ArrowLeft size={16} />
                Back to Home
              </Link>
            </motion.div>

            <motion.h2 variants={itemVariants}>Sign In</motion.h2>
            <motion.p variants={itemVariants} className="form-subtitle">
              Enter your credentials to access your account
            </motion.p>

            {/* Error Message */}
            {(localError || authError) && (
              <motion.div
                className="error-alert"
                variants={itemVariants}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <AlertCircle size={18} />
                <span>{localError || authError}</span>
              </motion.div>
            )}

            {/* Email Field */}
            <motion.div className="form-group" variants={itemVariants}>
              <label htmlFor="email">Email Address</label>
              <div className={`input-wrapper ${errors.email ? 'error' : ''}`}>
                <Mail size={18} className="input-icon" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  disabled={isLoading}
                  required
                />
              </div>
              {errors.email && (
                <motion.p className="error-message" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  {errors.email}
                </motion.p>
              )}
            </motion.div>

            {/* Password Field */}
            <motion.div className="form-group" variants={itemVariants}>
              <label htmlFor="password">Password</label>
              <div className={`input-wrapper ${errors.password ? 'error' : ''}`}>
                <Lock size={18} className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <motion.p className="error-message" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  {errors.password}
                </motion.p>
              )}
            </motion.div>

            {/* Remember Me & Forgot Password */}
            <motion.div className="form-options" variants={itemVariants}>
              <label className="checkbox">
                <input type="checkbox" disabled={isLoading} />
                <span>Remember me</span>
              </label>
              <Link to="/password-reset" className="forgot-link">
                Forgot password?
              </Link>
            </motion.div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={isLoading}
              variants={itemVariants}
              whileHover={!isLoading ? { scale: 1.02 } : {}}
              whileTap={!isLoading ? { scale: 0.98 } : {}}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
              {!isLoading && <ArrowRight size={18} />}
            </motion.button>

            {/* Sign Up Link */}
            <motion.p className="signup-prompt" variants={itemVariants}>
              Don't have an account?{' '}
              <Link to="/signup" className="signup-link">
                Create one now
              </Link>
            </motion.p>
          </motion.form>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default SignIn;